import { parseCustomReportConfiguration } from "#batteries-included-components/Layouts/CustomReport/CustomReportConfigurationPanel/CustomReportConfigurationPanel.helpers";
import {
  CustomReportVariantType,
  CustomReportVariants,
  getCustomReportFilters,
  getCustomReportGroupBy,
} from "#batteries-included-components/Layouts/CustomReport/CustomReportDetailLayout.helpers";
import {
  getCustomReportDefaultSort,
  useEquipmentEmissionsHeaders,
  useEquipmentRecordsHeaders,
  useFacilityRecordsHeaders,
  useFlowRecordsHeaders,
  useOutputHeaders,
} from "#batteries-included-components/Panels/TablePanels/CustomReportTablePanel/CustomReportTablePanel.helpers";
import {
  useExportCalculatorResults,
  useListCalculatorResults,
} from "#hooks/adapters/useCalculatorResults";
import { useExportRecords, useListRecords } from "#hooks/adapters/useRecords";
import { useTableSortingAndPagination } from "#redux/reducers/tableStateReducer";
import {
  Button,
  DataTablePanel,
  HeaderType,
  StorageKeys,
  useFilters,
} from "@validereinc/common-components";
import {
  AssetType,
  CalculatorResultType,
  RecordType,
} from "@validereinc/domain";
import { uniqBy } from "es-toolkit/compat";
import React, { useEffect } from "react";

export const CustomReportTablePanel = ({
  variant,
  configStorageKey,
  filterConfigStorageKey,
  tableConfigStorageKey,
  tableStateStorageKey,
}: Pick<
  StorageKeys,
  "filterConfigStorageKey" | "tableConfigStorageKey" | "tableStateStorageKey"
> & {
  variant: CustomReportVariantType;
  configStorageKey: string;
}) => {
  const [filters] = useFilters(filterConfigStorageKey);
  const [config] = useFilters(configStorageKey);
  const { rows, outputs, assetType } = parseCustomReportConfiguration(config);
  const [tableState, updateTableState] = useTableSortingAndPagination(
    getCustomReportDefaultSort(variant, assetType, rows),
    { ...filters, ...config },
    tableStateStorageKey
  );

  const apiParams = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    filters: getCustomReportFilters(variant, assetType, config, filters),
    groupBy: getCustomReportGroupBy(variant, assetType, rows),
  };

  const isEnabled = !!(
    rows?.length &&
    outputs?.length &&
    (variant === CustomReportVariants.EMISSIONS ||
      (variant === CustomReportVariants.RECORDS && !!assetType))
  );

  // @ts-expect-error TODO: fix filter typing
  const recordsQuery = useListRecords(apiParams, {
    enabled: variant === CustomReportVariants.RECORDS && isEnabled,
  });

  const resultsQuery = useListCalculatorResults(
    // @ts-expect-error TODO: fix filter typing
    { ...apiParams, pageDisableTotal: true },
    {
      enabled: variant === CustomReportVariants.EMISSIONS && isEnabled,
    }
  );

  const exportParams = {
    ...apiParams,
    groupBy: getCustomReportGroupBy(variant, assetType, rows, true),
  };

  const exportRecords = useExportRecords(exportParams);
  // @ts-expect-error TODO: fix filter typing
  const exportResults = useExportCalculatorResults(exportParams);

  const { isLoading: isLoadingEquipmentHeaders, headers: equipmentHeaders } =
    useEquipmentEmissionsHeaders();
  const {
    isLoading: isLoadingFlowRecordsHeaders,
    headers: flowRecordsHeaders,
  } = useFlowRecordsHeaders({
    enabled:
      variant === CustomReportVariants.RECORDS && assetType === AssetType.FLOW,
  });
  const {
    isLoading: isLoadingEquipmentRecordsHeaders,
    headers: equipmentRecordsHeaders,
  } = useEquipmentRecordsHeaders({
    enabled:
      variant === CustomReportVariants.RECORDS &&
      assetType === AssetType.EQUIPMENT,
  });
  const {
    isLoading: isLoadingFacilityRecordsHeaders,
    headers: facilityRecordsHeaders,
  } = useFacilityRecordsHeaders({
    enabled:
      variant === CustomReportVariants.RECORDS &&
      assetType === AssetType.FACILITY,
  });
  const { isLoading: isLoadingOutputHeaders, headers: outputHeaders } =
    useOutputHeaders(variant, outputs);

  useEffect(() => {
    const newTableState = getCustomReportDefaultSort(variant, assetType, rows);

    if (
      !newTableState.sortBy ||
      !newTableState.sortDirection ||
      (tableState.sortBy === newTableState.sortBy &&
        tableState.sortDirection === newTableState.sortDirection)
    )
      return;

    /** Reset the sort params if the rows change */
    updateTableState(newTableState);
  }, [variant, rows, assetType]);

  // REVIEW: Bundle this into an IIFE or something? Types aren't disambiguated by the table
  let exportQuery: ReturnType<typeof useExportRecords> | undefined;
  let items: RecordType[] | CalculatorResultType[] = [];
  let rowHeaders:
    | Array<HeaderType<CalculatorResultType>>
    | Array<HeaderType<RecordType>> = [];
  let isLoading = false;
  let total = 0;

  switch (variant) {
    case CustomReportVariants.EMISSIONS:
      items = resultsQuery.data?.data ?? [];
      isLoading =
        resultsQuery.isLoading ||
        isLoadingEquipmentHeaders ||
        isLoadingOutputHeaders;
      total = resultsQuery.data?.total_entries ?? 0;
      rowHeaders = equipmentHeaders;
      exportQuery = exportResults;
      break;
    case CustomReportVariants.RECORDS:
    default:
      items = recordsQuery.data?.data ?? [];
      isLoading =
        recordsQuery.isLoading ||
        (assetType === AssetType.FLOW && isLoadingFlowRecordsHeaders) ||
        (assetType === AssetType.EQUIPMENT &&
          isLoadingEquipmentRecordsHeaders) ||
        (assetType === AssetType.FACILITY && isLoadingFacilityRecordsHeaders) ||
        isLoadingOutputHeaders;
      total = recordsQuery.data?.total_entries ?? 0;
      rowHeaders =
        assetType === AssetType.FLOW
          ? flowRecordsHeaders
          : assetType === AssetType.EQUIPMENT
            ? equipmentRecordsHeaders
            : assetType === AssetType.FACILITY
              ? facilityRecordsHeaders
              : [];
      exportQuery = exportRecords;
      break;
  }

  const headers = uniqBy(
    [
      ...(rows?.flatMap((selectedKey: string) => {
        const header = rowHeaders.find(({ key }) => key === selectedKey);
        return header ? [header] : [];
      }) ?? []),
      ...outputHeaders,
    ],
    ({ key }) => key
  );

  // action row for Records
  const actionRow = exportQuery
    ? [
        <Button
          key="export-custom-report"
          variant="outline"
          onClick={() => exportQuery?.mutate()}
          isLoading={exportQuery?.isLoading}
        >
          Export
        </Button>,
      ]
    : [];

  return (
    <DataTablePanel
      storageKey={tableConfigStorageKey}
      panelProps={{ actionRow, title: "Results", style: { marginBottom: 0 } }}
      dataTableProps={{
        showRowNumbers: true,
        getItemId: (item) => item.id,
        isLoading: isEnabled && isLoading,
        items: isEnabled ? (items as RecordType[]) : ([] as RecordType[]),
        headers: headers as Array<HeaderType<RecordType>>,
        onSortChange: updateTableState,
        onPaginationChange: updateTableState,
        sorting: {
          sortBy: tableState.sortBy,
          sortDirection: tableState.sortDirection,
        },
        pagination: {
          page: tableState.page,
          pageSize: tableState.pageSize,
          total,
        },
        emptyStateProps: {
          suggestion: assetType
            ? "Please try adjusting your filters"
            : "Select an asset type to get started",
        },
      }}
    />
  );
};
